import { TrackImpressionDirective } from '@analytics/events';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  input,
  Input,
  output,
  Output,
} from '@angular/core';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { InnogyComponentRendering } from '@core/jss-models';
import type { StreetAddress } from '@essent/new-customer';
import type { Offer } from '@innogy/become-a-customer/shared/interfaces';
import { ConsumptionValuesVM } from '@innogy/become-a-customer/shared/interfaces';
import { OfferSaverButtonComponent } from '@innogy/offer-saver';
import type { CurrentContract } from '@innogy/retention/store';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { EnergyType } from '@integration/base-models';
import { RichTextWrapperComponent } from '@legacy/common-ui-utility-components';
import { TranslatePipe } from '@ngx-translate/core';
import { QuestionnaireResults } from '@retention/shared';
import { getDayOfYear } from 'date-fns';

import { CommodityBestOfferListComponent } from '../commodity-best-offer-list/commodity-best-offer-list.component';
import { EdConsumerRetentionCommodityOfferComponent } from '../commodity-offer-brand/ed-consumer-retention-commodity-offer/ed-consumer-retention-commodity-offer.component';
import { EssentConsumerRetentionCommodityOfferComponent } from '../commodity-offer-brand/essent-consumer-retention-commodity-offer/essent-consumer-retention-commodity-offer.component';
import { CommodityOfferCalculationBasedOnComponent } from '../commodity-offer-calculation-based-on/commodity-offer-calculation-based-on.component';
import { CommodityOfferHeaderComponent } from '../commodity-offer-header/commodity-offer-header.component';
import { CommodityOfferListComponent } from '../commodity-offer-list/commodity-offer-list.component';
import { CommodityOfferContent } from '../commodity-offers.model';
import { getSpecificOfferValueFromOffer } from '../utils';

@Component({
  selector: 'wl-vertical-commodity-offer',
  templateUrl: './vertical-commodity-offer.component.html',
  styleUrls: [
    './vertical-commodity-offer.component.ed.scss',
    './vertical-commodity-offer.component.essent.scss',
  ],
  standalone: true,
  imports: [
    CommodityOfferHeaderComponent,
    EssentConsumerRetentionCommodityOfferComponent,
    EdConsumerRetentionCommodityOfferComponent,
    CommodityOfferCalculationBasedOnComponent,
    CommodityBestOfferListComponent,
    CommodityOfferListComponent,
    OfferSaverButtonComponent,
    RichTextWrapperComponent,
    AutomationIdDirective,
    TranslatePipe,
    TrackImpressionDirective,
    CommonModule,
  ],
})
export class VerticalCommodityOfferComponent {
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() public rendering!: InnogyComponentRendering;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() content?: CommodityOfferContent;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() showMemberGetMember = false;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() offers?: Offer[];
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() consumptionValues?: ConsumptionValuesVM;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() supplyAddress?: Partial<StreetAddress>;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() flowId?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() accountId?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() currentContract?: CurrentContract;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() hasSolarPanels = false;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() crossSellThuisvoordeelAddon = false;

  @Output() selectOffer = new EventEmitter<Offer>();
  @Output() openMoreInfo = new EventEmitter<Offer>();
  @Output() viewTariffs = new EventEmitter<Offer>();
  @Output() adjustCalculation = new EventEmitter<void>();

  readonly questionnaireResults = input<QuestionnaireResults | undefined>();

  showQuestionnaireExplanation =
    output<ReturnType<typeof getSpecificOfferValueFromOffer>>();
  selectVariableOffer = output();

  brand = inject(ENVIRONMENT_CONFIG).brand;
  segment = inject(ENVIRONMENT_CONFIG).segment;

  get showCurrentContract() {
    return !!(
      this.segment === 'consument' &&
      this.content?.showMyContract &&
      this.currentContract
    );
  }

  get currentContractImpression() {
    if (!this.currentContract) {
      return '';
    }

    let electricityProduct = 'n/a',
      electricityDuration = 'n/a',
      gasProduct = 'n/a',
      gasDuration = 'n/a';
    const hasElectricityProduct = this.currentContract.commodities.includes(
      EnergyType.ELECTRICITY
    );
    const hasGasProduct = this.currentContract.commodities.includes(
      EnergyType.GAS
    );

    if (hasElectricityProduct && hasGasProduct) {
      [electricityProduct, gasProduct] =
        this.currentContract.productTitle.split(' & ');
      electricityDuration = gasDuration = this.currentContract.duration;
    } else if (hasElectricityProduct) {
      electricityProduct = this.currentContract.productTitle;
      electricityDuration = this.currentContract.duration;
    } else if (gasProduct) {
      gasProduct = this.currentContract.productTitle;
      gasDuration = this.currentContract.duration;
    }

    return `Je huidige contract: <${electricityProduct}, ${electricityDuration}>, <${gasProduct}, ${gasDuration}>`;
  }

  /**
   * The switchback test turns on/off automatically every day for smoother test results from inbound calls
   * This will be removed after the test has been completed
   * @see https://jira.essent.nl/jira/browse/CTR-2141
   */
  get redeliverySwitchbackTestActive() {
    const testEnabled = this.content?.redeliveryNoticeSwitchbackTest;
    const dayOfYearIsOdd = getDayOfYear(new Date()) % 2 === 1;
    return this.hasSolarPanels && testEnabled && dayOfYearIsOdd;
  }
}
